import $ from 'jquery';

import AddressModal from 'chairisher/view/account/addressmodal';
import AddressModalHelper from 'chairisher/view/helper/addressmodal';
import AdminCard from 'chairisher/view/product/update/card/admin';
import AlerterView from 'chairisher/view/alerter';
import AppleView from 'chairisher/view/apple';
import AuthView from 'chairisher/view/authentication';
import BasicsCard from 'chairisher/view/product/update/card/basics';
import DetailsCard from 'chairisher/view/product/update/card/details';
import FacebookView from 'chairisher/view/facebook';
import FormFieldComponent from 'chairisher/component/form/field';
import HistoryUtils from 'chairisher/util/history';
import LogisticsCard from 'chairisher/view/product/update/card/logistics';
import ModalFactory from 'chairisher/factory/modal';
import TermsOfServiceAndPrivacyPolicyView from 'chairisher/view/account/tosapp';

import executeRecaptcha from 'chairisher/view/helper/recaptcha';
import renderGoogleSignin from 'chairisher/view/socialauth/google';

import { getLoginUrl, getRecaptchaActionName, isAuthenticated, isStaff } from 'chairisher/context/auth';
import { getSupportErrorText } from 'chairisher/context/error';
import { syncListAriaHidden } from 'chairisher/util/accessibility';
import {
    AmplitudeEventProperty,
    logAmplitudeButtonClickEvent,
    logAmplitudeClickEvent,
    logAmplitudeEvent,
} from 'chairisher/util/analytics';
import { formatProductUpdateData } from 'chairisher/util/productdata';
import { isForbidden } from 'chairisher/util/status';
import {
    getAttrValuesWithModals,
    getDeclinedTypeCode,
    getIncompleteTypeCode,
    getUnsavedChangesConfirmationText,
    setMetaObject,
    setProductObject,
} from 'chairisher/context/listingflow';

const { launchAddressModal } = AddressModalHelper;

/**
 * View that assists in creating / updating products.
 *
 * @param {Object} settings
 * @param {Object} settings.metaObject @see this.metaObject
 * @param {Object} settings.productObject @see this.productObject
 *
 * @constructor
 * @class ProductUpdateView
 */
const ProductUpdateView = function ProductUpdateView(settings) {
    const options = $.extend(
        {
            metaObject: {},
            productObject: {},
        },
        settings,
    );

    //
    // Data
    //

    this.metaObject = options.metaObject;
    this.productObject = options.productObject;

    //
    // Cards
    //

    this.basicsCard = new BasicsCard();
    this.detailsCard = new DetailsCard();
    this.logisticsCard = new LogisticsCard();

    this.cardIdToCardInstanceMap = {
        '#js-basic-fields': this.basicsCard,
        '#js-details-fields': this.detailsCard,
        '#js-logistics-fields': this.logisticsCard,
    };

    if (isStaff()) {
        this.adminCard = new AdminCard();
        this.cardIdToCardInstanceMap['#js-admin-fields'] = this.adminCard;
    }

    //
    // Form
    //
    this.$formEl = $('.js-product-update-form');
    this.formAction = this.$formEl.attr('action');
    this.formData = {};

    this.pickupAddressModal = new AddressModal();
    this.pickupAddressModal.bind();

    this.basicsCard.setImages(this.productObject.images.value);
    this.detailsCard.setBrandValues(this.productObject.brands.value);

    this.updateUi();
    this.bind();

    $.each(this.cardIdToCardInstanceMap, (_, card) => {
        card.updateSuggestions(this.productObject);
    });

    $.each(this.detailsCard.imageSelectComponentNames, (_, fieldName) => {
        this.detailsCard.buildImageSelectTextContainers(fieldName);
        this.detailsCard.rebuildImageSelectLabels(fieldName);
    });

    this.setVariantFormData(this.metaObject);

    this.logisticsCard.setPriceSelectOptions(this.productObject);

    if (!isAuthenticated()) {
        this.bindAuthForm();
    }

    logAmplitudeEvent('screen - listing form', {
        [AmplitudeEventProperty.TYPE]: this.metaObject.id ? 'edit' : 'create',
    });
};

/**
 * Instance of the AdminCard
 *
 * @type AdminCard
 */
ProductUpdateView.prototype.adminCard = null;

/**
 * Instance of the BasicsCard
 *
 * @type BasicsCard
 */
ProductUpdateView.prototype.basicsCard = null;

/**
 * Instance of the DetailsCard
 *
 * @type DetailsCard
 */
ProductUpdateView.prototype.detailsCard = null;

/**
 * Flag that indicates the user has tried to submit this listing during their time on this screen
 *
 * @type {boolean}
 */
ProductUpdateView.prototype.isInReviewMode = false;

/**
 * Instance of the LogisticsCard
 *
 * @type LogisticsCard
 */
ProductUpdateView.prototype.logisticsCard = null;

/**
 * An object that contains meta information related to this UI that comes from the server
 *
 * @type {Object}
 */
ProductUpdateView.prototype.metaObject = null;

/**
 * @type {$.Deferred}
 */
ProductUpdateView.prototype.mostRecentValidateXhr = null;

/**
 * @typedef {Object} Card
 */

/**
 * Mapping of card id to Card object instance
 *
 * @type {Array.<Card>}
 */
ProductUpdateView.prototype.cardIdToCardInstanceMap = null;

/**
 * The modal for displaying a user's saved pickup addresses or creating a new pickup address
 *
 * @type {AddressModal}
 */
ProductUpdateView.prototype.pickupAddressModal = null;

/**
 * An object-based representation of the Product being created/updated that comes from the server
 *
 * @type {Object}
 */
ProductUpdateView.prototype.productObject = null;

/**
 * Binds actions to the various elements that comprise the view
 *
 */
ProductUpdateView.prototype.bind = function bind() {
    $('.js-pickup-address-btn').on('click', (e) => {
        e.preventDefault();

        const $target = $(e.currentTarget);
        launchAddressModal({
            isChangeable: $target.attr('data-modal-is-changeable') === 'true',
            modal: this.pickupAddressModal,
            type: $target.attr('data-modal-type'),
            url: $target.attr('href'),
        });
    });

    this.pickupAddressModal.$el.on('addressmodal:change', (_, data) => {
        if (data && data.accountAddress) {
            const { accountAddress } = data;
            const { address: pickupAddress } = accountAddress;

            const accountAddressKeys = ['name', 'phone_number'];
            const pickupAddressKeys = [
                'city',
                'country_name',
                'id',
                'postal_code',
                'state',
                'street_address_line_1',
                'street_address_line_2',
            ];

            accountAddressKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(accountAddress, key)) {
                    const { value } = accountAddress[key];
                    $(`[name="pickup_${key}"]`).val(value).trigger('change');
                    $(`.js-pickup-address-${key.replaceAll('_', '-')}`).text(value);
                }
            });

            pickupAddressKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(pickupAddress, key)) {
                    const { value } = pickupAddress[key];
                    if (key === 'id') {
                        $('[name="pickup_address_id"]').val(value).trigger('change');
                    } else {
                        $(`.js-pickup-address-${key.replaceAll('_', '-')}`).text(value);
                    }
                }
            });
        }
    });

    $('.js-pro-tip-link').on('click', (e) => {
        e.preventDefault();

        const dataKey = $(e.target).data('pro-tip-key');
        const proTipUrl = this.metaObject.tip_urls[dataKey];

        logAmplitudeClickEvent('Pro Tip', dataKey, proTipUrl);

        const $modal = ModalFactory.createModal();
        $modal.addClass('pro-tip-modal');
        $modal.find('.js-modal-body').html($(`.js-tip[data-pro-tip-key="${dataKey}"]`).clone());
        $modal.find('.js-title').remove();
        $modal.modal('show');
        $modal.on('hidden.bs.modal', () => {
            $modal.remove();
        });
    });

    $('.js-drafts-cancel').on('click', () => {
        const $collapse = $('#js-drafts-collapse');
        $collapse.collapse('hide');
        $collapse.on('hidden.bs.collapse', () => {
            $collapse.remove();
        });
    });

    this.$formEl.on('submit', (e) => {
        e.preventDefault(); // force users to click a button
    });

    $('.js-wizard-steps').on('click', '[data-destination]', (e) => {
        e.preventDefault();

        const card = this.cardIdToCardInstanceMap[$(e.currentTarget).data('destination')];

        if (card) {
            $('html, body').animate({
                scrollTop: card.getCardEl().offset().top,
            });
        }
    });

    $('.js-image-preview-trigger').on('click', () => {
        $('.js-image-preview').toggleClass('expanded-vertical');
    });

    //
    // Confirm Page Exit
    //
    $(window).on('beforeunload', (e) => {
        if (this.hasUnsavedChanges()) {
            e.preventDefault();
            return getUnsavedChangesConfirmationText();
        }
        return undefined;
    });

    //
    // ToS Modal
    //

    const tosappView = new TermsOfServiceAndPrivacyPolicyView();
    tosappView.showModal(true);

    //
    // Bind card change events
    //

    const handleCardChange = (_, dataObject) => {
        Object.entries(dataObject).forEach(([k, v]) => {
            this.formData[k] = v;
        });

        const successCallback = (data) => {
            if ('title' in dataObject || 'description' in dataObject || 'categories' in dataObject) {
                this.getCards().forEach((card) => {
                    card.updateSuggestions(data.product);
                });
            }
        };

        if ('action' in dataObject) {
            const stateAttr = dataObject.action.state_attributes[0];
            const stateAttrCode = stateAttr.type_code;
            const shouldPopModal = stateAttr.value && $.inArray(stateAttrCode, getAttrValuesWithModals()) !== -1;

            if (shouldPopModal) {
                const $modal = $(`.js-transition-confirmation-${stateAttrCode}`);
                $modal.modal('show');
            } else {
                this.save().done(successCallback);
            }
        } else {
            this.validate().done(successCallback);
        }
    };

    const cards = this.getCards();
    for (let i = 0; i < cards.length; i += 1) {
        cards[i].getCardEl().on('card:change', handleCardChange);
    }

    //
    // Admin Transitions
    // todo: (CHAIR-7407) move this to card/admin.js
    //

    const resetAndCloseConfirmationModal = function resetAndCloseConfirmationModal($modal) {
        $modal.modal('hide');
        $modal.find('input:checked').prop('checked', false);
        $modal.find('input[type=text]').val('');
    };

    $('.js-btn-incomplete').on('click', (e) => {
        e.preventDefault();
        const incompleteReasonCodeValues = [];
        const incompleteReasonNoteValue = $('[name=incomplete_reason_note]').val();
        const $incompleteReasonCodes = $('[name=incomplete_reason_codes]');

        $.each($incompleteReasonCodes.filter(':checked'), (i, el) => {
            incompleteReasonCodeValues.push(el.value);
        });

        const action = this.formData.action || {};

        const stateAttributes = action.state_attributes;

        if (stateAttributes && stateAttributes.length) {
            const incompleteTypeCode = getIncompleteTypeCode();

            for (let i = 0; i < stateAttributes.length; i += 1) {
                const attr = stateAttributes[i];
                if (attr.type_code === incompleteTypeCode) {
                    attr.incomplete_reason_codes = incompleteReasonCodeValues;
                    attr.incomplete_reason_note = incompleteReasonNoteValue;
                    break;
                }
            }
        }
        this.formData.action = action;

        const $modal = $(e.target).closest('.modal');

        this.submit().done(() => {
            resetAndCloseConfirmationModal($modal);
        });
    });

    const onAdminCancelClick = (e) => {
        e.preventDefault();
        const $modal = $(e.target).closest('.modal');
        resetAndCloseConfirmationModal($modal);
    };
    $('.js-btn-cancel').on('click', onAdminCancelClick);

    $('.js-btn-decline').on('click', (e) => {
        e.preventDefault();
        const declinedReasonCodeValues = [];
        const declinedReasonNoteValue = $('[name=declined_reason_note]').val();
        const $declineReasonCodes = $('[name=declined_reason_codes]');

        $.each($declineReasonCodes.filter(':checked'), (i, el) => {
            declinedReasonCodeValues.push(el.value);
        });

        const action = this.formData.action || {};
        const stateAttributes = action.state_attributes;

        if (stateAttributes && stateAttributes.length) {
            const declinedTypeCode = getDeclinedTypeCode();

            for (let i = 0; i < stateAttributes.length; i += 1) {
                const attr = stateAttributes[i];
                if (attr.type_code === declinedTypeCode) {
                    attr.declined_reason_codes = declinedReasonCodeValues;
                    attr.declined_reason_note = declinedReasonNoteValue;
                    break;
                }
            }
        }
        this.formData.action = action;

        const $modal = $(e.target).closest('.modal');

        this.submit().done(() => {
            resetAndCloseConfirmationModal($modal);
        });
    });

    //
    // Duplication
    //

    let isDuplicateRequestInFlight = false;
    $('.js-duplicate').on('click', (e) => {
        e.preventDefault();

        const duplicateUrl = $(e.currentTarget).attr('href');
        if (!isDuplicateRequestInFlight && duplicateUrl) {
            isDuplicateRequestInFlight = true;
            $.ajax({
                url: duplicateUrl,
                method: 'POST',
            })
                .done((data) => {
                    window.location = data.redirect_url;
                })
                .fail(() => {
                    isDuplicateRequestInFlight = false;
                });
        }
    });
};

/**
 * Binds the account create/login form
 *
 */
ProductUpdateView.prototype.bindAuthForm = function bindAuthForm() {
    AppleView.loadAuthButton();
    FacebookView.bind();
    renderGoogleSignin();

    const authView = new AuthView();
    executeRecaptcha(getRecaptchaActionName());

    const $form = $('.js-update-form-auth');
    const $formParent = $form.parent();

    $form.on('submit', (e) => {
        e.preventDefault();
        authView
            .submit(getLoginUrl(), $form.serialize())
            .done(() => {
                $formParent.addClass('retracted');
                window.setTimeout(() => {
                    $formParent.remove();
                    this.validate().done(() => {
                        $('.js-product-update-form.js-disabled').removeClass('disabled js-disabled');
                    });
                }, 1000);
            })
            .fail((data) => {
                const { errors } = data.responseJSON;
                if (errors && errors.length) {
                    const errorMsgs = [];

                    $.each(errors, (i, error) => {
                        errorMsgs.push(
                            $('<li></li>', {
                                class: 'error-item',
                                text: error.message,
                            }),
                        );
                    });
                    const $errors = $formParent.children('.js-section-error');
                    const $errorList = $errors.find('.js-error-list');
                    $errorList.html(errorMsgs);
                    $errors.removeClass('hidden');
                }
            });
    });
};

/**
 * @returns {Array.<Card>} An iterable collection containing all cards
 */
ProductUpdateView.prototype.getCards = function getCards() {
    const vals = [];
    $.each(this.cardIdToCardInstanceMap, (id, instance) => {
        vals.push(instance);
    });
    return vals;
};

/**
 * @returns {boolean} True indicates there are either meta or product errors
 */
ProductUpdateView.prototype.hasErrors = function hasErrors() {
    let hasErrorsPresent = this.metaObject.errors;

    if (!hasErrorsPresent) {
        $.each(this.getCards(), (_, card) => {
            if (card.hasErrors()) {
                hasErrorsPresent = true;
                // break out of the callback
            }
        });
    }

    return hasErrorsPresent;
};

/**
 * @returns {boolean} True indicates there are messages in `this.metaObject` to display
 */
ProductUpdateView.prototype.hasMessages = function hasMessages() {
    const { messages } = this.metaObject;
    return !!(messages && messages.length);
};

/**
 * @returns {boolean} True indicates there are unsaved changes to persist
 */
ProductUpdateView.prototype.hasUnsavedChanges = function hasUnsavedChanges() {
    return Object.keys(this.formData).length > 0;
};

/**
 * @param {Objects} metaObject The meta object
 */
ProductUpdateView.prototype.setVariantFormData = function setVariantFormData(metaObject) {
    if (metaObject.variant_parent_id) {
        this.formData.variant_parent_id = metaObject.variant_parent_id;
    }

    if (metaObject.variant_attribute_group_code) {
        this.formData.variant_attribute_group_code = metaObject.variant_attribute_group_code;
    }
};

/**
 * Sends data to the server.
 * Whether or not the data is saved is up to what the caller sets in dataObject.meta.should_save...
 *
 * @param {Object} dataObject The data to persist
 * @param {boolean=} optShouldRemoveSavedFields Optional flag to indicate if saved fields should be removed
 * after submission
 *
 * @returns {$.Deferred}
 */
ProductUpdateView.prototype.persist = function persist(dataObject, optShouldRemoveSavedFields) {
    const shouldRemoveSavedFields = optShouldRemoveSavedFields === undefined ? true : !!optShouldRemoveSavedFields;

    const payload = {
        ...dataObject,
        meta: {
            ...(dataObject.meta || {}),
            should_include_review_errors: this.isInReviewMode,
        },
    };

    return $.ajax({
        contentType: 'application/json',
        type: this.$formEl.attr('method'),
        data: JSON.stringify(payload),
        url: this.formAction,
    })
        .done((data) => {
            if (data) {
                this.metaObject = data.meta;
                this.productObject = data.product;

                setMetaObject(this.metaObject);
                setProductObject(this.productObject);

                const meta = this.metaObject;

                if (meta.exit_url) {
                    $(window).off('beforeunload'); // just redirect; no confirmation needed
                    window.location.href = meta.exit_url;
                } else {
                    this.updateUi();

                    // update the URL to point to the edit URL...
                    const updateUrl = data.meta.update_url;

                    if (updateUrl) {
                        HistoryUtils.replaceState({}, document.title, updateUrl);
                        this.formAction = updateUrl;
                    }

                    if (this.hasMessages()) {
                        let msg = '';
                        for (let i = 0; i < meta.messages.length; i += 1) {
                            if (i > 0) {
                                msg += '\n\n';
                            }

                            msg += meta.messages[i].display;
                        }

                        AlerterView.info(msg);
                    }

                    if (shouldRemoveSavedFields) {
                        Object.keys(this.formData).forEach((k) => {
                            // FIXME: CHAIR-14215 - Remove logic preventing pickup address from being removed
                            // Do not remove any pickup address fields from form since they need to be sent
                            // together to determine if it is valid or not
                            const addressFields = [
                                'pickup_street_address',
                                'pickup_city',
                                'pickup_state',
                                'pickup_country_code',
                                'pickup_postal_code',
                            ];

                            if (
                                !addressFields.includes(k) &&
                                k in this.productObject &&
                                'errors' in this.productObject[k] === false
                            ) {
                                delete this.formData[k];
                            }
                        });
                    }
                    this.toggleButtons(false);

                    this.setVariantFormData(meta);
                }
            }
        })
        .fail((data) => {
            if (data.statusText === 'abort') {
                return;
            }
            if (isForbidden(data.status)) {
                AlerterView.error(getSupportErrorText());
            }
        })
        .always((data) => {
            if (data.statusText !== 'abort') {
                delete this.formData.state;
            }
        });
};

/**
 * Attempts to save the current unchanged fields. Does not ask for required-ness errors.
 *
 * @returns {$.Deferred}
 */
ProductUpdateView.prototype.save = function save() {
    const dataObject = formatProductUpdateData(this.formData, this.metaObject);
    dataObject.meta.should_save = true;

    this.toggleButtons(true);

    return this.persist(dataObject).always(() => {
        this.toggleButtons(false);
    });
};

/**
 * Submits data to the server to be saved and validated for both syntactic errors and requiredness errors
 *
 * @returns {$.Deferred}
 */
ProductUpdateView.prototype.submit = function submit() {
    const dataObject = formatProductUpdateData(this.formData, this.metaObject);
    dataObject.meta.should_include_review_errors = true;
    dataObject.meta.should_save = true;

    this.isInReviewMode = true;

    this.toggleButtons(true);

    return this.persist(dataObject);
};

ProductUpdateView.prototype.updateImagePreview = function updateImagePreview() {
    const imgs = [];
    const $productImages = $('.js-image > img');
    $.each($productImages, (i) => {
        imgs.push(
            $productImages.eq(i).clone().attr({
                class: 'carousel-slide js-carousel-slide',
                id: '', // prevent duplicate IDs
            }),
        );
    });
    $('.js-image-preview .js-carousel-inner').html(imgs);
};

ProductUpdateView.prototype.updateNav = function updateNav() {
    const $productImages = this.basicsCard.getCardEl().find('.js-image > img');
    const hasProductImage = $productImages.length > 0;

    const $mastheadContainer = $('.js-masthead-container');
    const $img = $mastheadContainer.find('.js-product-image');
    let imgSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///////yH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

    if (hasProductImage) {
        imgSrc = $productImages.eq(0).attr('src');
    }

    $img.attr('src', imgSrc);

    $mastheadContainer.toggleClass('has-image', !!hasProductImage);

    let title = this.productObject.title.value;
    if (title && title.length > 60) {
        title = `${title.substring(0, 57)}...`;
    }
    const $title = $mastheadContainer.find('.js-product-title');
    $title.text(title || '').toggleClass('hidden', !title);
};

ProductUpdateView.prototype.updateProTips = function updateProTips() {
    const $dimensionTipCard = $('.js-tip-dimension');
    const dimensionHeightUrl = this.metaObject.tip_urls.dimension_height;
    $dimensionTipCard.toggleClass('hidden', !dimensionHeightUrl);
    if (dimensionHeightUrl && $dimensionTipCard.data('url') !== dimensionHeightUrl) {
        const onDimensionTipHtmlLoaded = (data) => {
            $dimensionTipCard.html(data);
            $dimensionTipCard.data('url', dimensionHeightUrl);
        };
        $.ajax({ url: dimensionHeightUrl }).done(onDimensionTipHtmlLoaded);
    }
};

/**
 * Updates the UI to reflect the product's current state
 *
 */
ProductUpdateView.prototype.updateUi = function updateUi() {
    this.updateButtons();
    this.updateNav();
    this.updateProTips();

    $('.js-status-display').text(this.metaObject.state.display);

    $.each(this.getCards(), (_, card) => {
        card.updateFieldsFromDataObject(this.productObject);
        card.updateWarningsFromDataObject(this.productObject);
        card.updateInstructionsFromDataObject(this.productObject);
        card.updateMetaFieldsFromDataObject(this.metaObject);
        card.updateCompletionIndicators();
    });

    this.updateImagePreview();
    this.updateMetaErrorsAndWarnings();

    if (isStaff()) {
        const staffActions = this.metaObject.state.staff_actions;
        if (staffActions) {
            this.adminCard.updateStaffActions(staffActions);
        }
    }
};

/**
 * Submits data to the server to be validated for syntactic errors. Data will *not* be persisted on the server.
 *
 * @returns {$.Deferred}
 */
ProductUpdateView.prototype.validate = function validate() {
    const dataObject = formatProductUpdateData(this.formData, this.metaObject);

    if (this.mostRecentValidateXhr) {
        this.mostRecentValidateXhr.abort();
    }

    this.mostRecentValidateXhr = this.persist(dataObject, false);

    return this.mostRecentValidateXhr;
};

/**
 * @param {boolean} shouldDisable True disables the save/submit buttons, false enables them
 */
ProductUpdateView.prototype.toggleButtons = function toggleButtons(shouldDisable) {
    $(['button', '.btn'].join(',')).prop('disabled', shouldDisable);
};

/**
 * Updates submit button visibility and value based on data from the server
 */
ProductUpdateView.prototype.updateButtons = function updateButtons() {
    const $fragment = $('<div></div>');
    const $fragmentLinks = $('<div></div>');
    const saveActions = this.metaObject.state.save_actions;

    $.each(saveActions, (i, saveAction) => {
        const $saveBtn = $('<button></button>', {
            class: 'btn btn-primary',
            text: saveAction.display,
            type: 'button',
        });
        const $saveLink = $saveBtn.clone().addClass('btn-link');

        const shouldIncludeReviewErrors =
            saveAction.action_value && saveAction.action_value.should_include_review_errors;
        if (!shouldIncludeReviewErrors) {
            $saveBtn.addClass('btn-minor');
        }

        $.each([$saveBtn, $saveLink], (_, $b) => {
            $b.on('click', () => {
                logAmplitudeButtonClickEvent($saveBtn.text().toLowerCase(), {
                    [AmplitudeEventProperty.POSITION]: 'listing form',
                });
                this.formData.action = saveAction.action_value;

                this.save();
            });
        });

        $fragment.append($saveBtn);
        $fragmentLinks.append($saveLink);
    });
    $('.js-save-actions').html($fragment.children());
    $('.js-save-action-links').html($fragmentLinks.children());

    //
    // Duplicate Link
    //
    const duplicateUrl = this.metaObject.duplicate_url;
    const $duplicateLink = $('.js-duplicate');
    if (duplicateUrl) {
        $duplicateLink.attr('href', duplicateUrl);
    }
    $duplicateLink.toggleClass('hidden', !duplicateUrl);
};

/**
 * Updates the Meta errors and messages containers with fresh errors or warnings, if any
 */
ProductUpdateView.prototype.updateMetaErrorsAndWarnings = function updateMetaErrorsAndWarnings() {
    const updateMessages = ($wrapper, $errorList, messages) => {
        $wrapper.toggleClass('hidden', messages.length === 0);
        $errorList.html(FormFieldComponent.marshalMessageList(messages));
        syncListAriaHidden($errorList[0]);
    };

    updateMessages($('.js-meta-errors-wrapper'), $('.js-meta-errors ul'), this.metaObject.errors || []);

    updateMessages($('.js-meta-warnings-wrapper'), $('.js-meta-warnings ul'), this.metaObject.warnings || []);
};

export default ProductUpdateView;
